import axios, { AxiosError } from 'axios';
import { LOCAL_STORAGE_KEYS } from '@/constants';
import type { TokenData, RefreshTokenResponse } from './types';

const API_URL_BASE = import.meta.env?.VITE_API_URL_BASE ?? '/api';

/**
 * Outcome of a refresh attempt.
 * - `auth`     → the refresh cookie was rejected (expired/revoked/user
 *                disabled). Tokens are cleared; the caller should send the
 *                operator to `/login`.
 * - `network`  → offline / timeout / 5xx cold start. Tokens are PRESERVED and
 *                the caller must NOT bounce to `/login`: the HttpOnly cookie is
 *                still valid server-side, so the session survives the blip and
 *                the next request simply tries again.
 * - `no_token` → nothing on record to refresh; treat as a sign-out.
 */
export type RefreshResult =
  | { ok: true; accessToken: string }
  | { ok: false; reason: 'auth' | 'network' | 'no_token' };

/**
 * Transient (non-auth) failure: the refresh cookie was never rejected, so the
 * session must be PRESERVED. Without this distinction every cold start, dropped
 * connection, and 502 looked exactly like a revoked session and threw the
 * operator back to the login screen mid-work.
 */
function isTransientError(error: unknown): boolean {
  if (error instanceof AxiosError) {
    return (
      error.code === 'ECONNABORTED' ||
      error.code === 'ETIMEDOUT' ||
      error.code === 'ERR_NETWORK' ||
      !error.response ||
      (error.response.status >= 500 && error.response.status < 600)
    );
  }
  return false;
}

/**
 * Name of the origin-wide lock serialising `/auth/refresh`.
 *
 * WHY A CROSS-TAB LOCK. The refresh token is an HttpOnly cookie shared by every
 * tab on the origin, but an in-page `isRefreshing` flag only ever guards ONE JS
 * context. Two tabs whose access tokens lapse together therefore each fire
 * `/auth/refresh` with the same cookie; the server rotates for the first and
 * sees the second present an already-revoked token, which it treats as a replay
 * and answers by revoking the operator's ENTIRE session chain — every tab, every
 * device. An admin portal is exactly where several tabs stay open, so this is a
 * routine occurrence rather than an edge case.
 */
const REFRESH_LOCK = 'df-portal.auth.refresh';

/**
 * Upper bound on waiting for the lock. Comfortably longer than the 15s request
 * timeout below, so a tab that is genuinely refreshing is never abandoned — this
 * only breaks the deadlock where the holder was frozen (bfcache) or otherwise
 * stopped without releasing.
 */
const LOCK_WAIT_MS = 20_000;

type LockManagerLike = {
  request: (
    name: string,
    options: { signal?: AbortSignal },
    fn: () => Promise<RefreshResult>
  ) => Promise<RefreshResult>;
};

function getLockManager(): LockManagerLike | null {
  const locks = (
    globalThis as unknown as { navigator?: { locks?: LockManagerLike } }
  ).navigator?.locks;
  return typeof locks?.request === 'function' ? locks : null;
}

/**
 * Run `fn` while holding the origin-wide refresh lock, so only one tab talks to
 * `/auth/refresh` at a time.
 *
 * Falls straight through where the Web Locks API is unavailable (older Safari,
 * a non-secure context, or the test/node environment). That degrades to the
 * previous in-page-only guard rather than failing — no worse than before.
 */
async function withCrossTabLock(
  fn: () => Promise<RefreshResult>
): Promise<RefreshResult> {
  const locks = getLockManager();
  if (!locks) return fn();

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOCK_WAIT_MS);
  try {
    return await locks.request(REFRESH_LOCK, { signal: controller.signal }, fn);
  } catch (error) {
    // The signal aborts only the WAIT, never `fn` itself — so reaching here
    // means we never held the lock and never sent anything. Report it as a
    // transient failure: the session stands, and the next request retries.
    // Refreshing anyway would be precisely the unguarded call this exists to
    // prevent.
    if (controller.signal.aborted) return { ok: false, reason: 'network' };
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

class TokenRefreshService {
  private isRefreshing = false;
  private refreshPromise: Promise<RefreshResult> | null = null;

  getToken(): TokenData | null {
    const tokenDataStr = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN_DATA);
    if (!tokenDataStr) return null;
    try {
      return JSON.parse(tokenDataStr) as TokenData;
    } catch {
      return null;
    }
  }

  setToken(tokenData: TokenData): void {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.TOKEN_DATA,
      JSON.stringify(tokenData)
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.ACCESS_TOKEN,
      tokenData.access_token
    );
  }

  removeToken(): void {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN_DATA);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.USER);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.SELECTED_TENANT);
  }

  /**
   * Refreshes the access token by calling `POST /v1/auth/refresh`.
   * The refresh token rides in an HttpOnly cookie (not visible to JS), so this
   * call sends an empty body with `withCredentials: true`. The server validates
   * the cookie, rotates it via `Set-Cookie`, and returns a new access token.
   *
   * IMPORTANT — single-flight invariant, at two levels. Concurrent callers in
   * THIS tab share the in-flight promise via the `refreshPromise` guard, and
   * concurrent TABS serialise on the `REFRESH_LOCK` Web Lock. Bypassing either
   * and calling `/auth/refresh` from parallel code paths triggers
   * replay-detection on the server (T9 chain-revoke) and logs the user out from
   * every device. Always go through `getValidToken()` or this service's guard.
   */
  async refresh(): Promise<RefreshResult> {
    // If already refreshing, return the existing promise (single-flight gate)
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    const currentToken = this.getToken();

    // Give up cleanly if there's no record we ever logged in.
    // getValidToken() only calls refresh after getToken() returns non-null,
    // so this is belt-and-braces only.
    if (!currentToken) {
      return { ok: false, reason: 'no_token' };
    }

    this.isRefreshing = true;

    // Create and store the refresh promise. The lock is acquired INSIDE it so
    // same-tab callers still collapse onto one promise while waiting.
    this.refreshPromise = withCrossTabLock(() =>
      this.rotate(currentToken.access_token)
    ).finally(() => {
      this.isRefreshing = false;
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  /**
   * Perform the actual rotation. Only ever called while holding the cross-tab
   * lock, so at most one tab is in here at a time.
   */
  private async rotate(previousAccessToken: string): Promise<RefreshResult> {
    // Another tab may have rotated the cookie while we queued for the lock. In
    // that case the work is already done — re-sending would present the token
    // it just revoked, which is the replay this lock exists to avoid.
    const held = this.getToken();
    if (!held) {
      // Another tab signed out (or was rejected) and cleared storage.
      return { ok: false, reason: 'no_token' };
    }
    if (
      held.access_token !== previousAccessToken &&
      !this.isTokenExpired(held)
    ) {
      return { ok: true, accessToken: held.access_token };
    }

    try {
      // Use a bare Axios request without interceptors to avoid circular deps.
      // Body is empty — the refresh token rides in the HttpOnly cookie.
      // withCredentials: true is required so the browser attaches the cookie.
      const response = await axios.post<RefreshTokenResponse>(
        `${API_URL_BASE}/v1/auth/refresh`,
        {},
        {
          timeout: 15000,
          withCredentials: true,
        }
      );

      const { value } = response.data;

      if (!response.data.succeeded || response.data.hasErrors) {
        throw new Error(
          'Token refresh failed: ' + response.data.errors.join(', ')
        );
      }

      // Backend returns camelCase: accessToken, tokenType, expiresIn.
      // No refreshToken in the response body — the rotated cookie arrives
      // automatically via Set-Cookie.
      const accessTtl = Number(value.expiresIn);
      // Mirror the server's REFRESH_TOKEN_TTL_DAYS=14 default. Keeping these
      // in sync prevents hasExpiredSession() from force-logging-out a user
      // whose HttpOnly cookie is still valid server-side.
      const refreshTtl = 14 * 24 * 60 * 60; // 14 days = 1209600 seconds

      const now = Math.floor(Date.now() / 1000);
      const newTokenData: TokenData = {
        access_token: value.accessToken,
        refresh_token: '',
        expiresIn: accessTtl,
        expiresAt: now + accessTtl,
        refreshTokenExpiresIn: refreshTtl,
        refreshTokenExpiresAt: now + refreshTtl,
      };

      this.setToken(newTokenData);

      return { ok: true, accessToken: value.accessToken };
    } catch (error) {
      // A blip must not end the session: the HttpOnly refresh cookie is still
      // valid server-side, so clearing local storage here would log the
      // operator out of a session the server would have happily renewed.
      if (isTransientError(error)) {
        return { ok: false, reason: 'network' };
      }
      this.removeToken();
      return { ok: false, reason: 'auth' };
    }
  }

  /** String|null wrapper for callers that only need the access token. */
  async refreshToken(): Promise<string | null> {
    const result = await this.refresh();
    return result.ok ? result.accessToken : null;
  }

  isTokenExpired(token?: TokenData): boolean {
    if (!token) {
      return true;
    }
    const currentTime = Math.floor(Date.now() / 1000);

    if (typeof token.expiresAt === 'number') {
      const ttl = token.expiresAt - currentTime;
      const buffer = Math.min(300, Math.max(5, Math.floor(ttl * 0.1)));
      return token.expiresAt <= currentTime + buffer;
    }

    return true;
  }

  isRefreshTokenExpired(token?: TokenData): boolean {
    if (!token) {
      return true;
    }
    const currentTime = Math.floor(Date.now() / 1000);

    if (typeof token.refreshTokenExpiresAt === 'number') {
      return token.refreshTokenExpiresAt <= currentTime;
    }

    return true;
  }

  async getValidToken(): Promise<string | null> {
    const currentToken = this.getToken();

    if (!currentToken) {
      return null;
    }

    if (!this.isTokenExpired(currentToken)) {
      return currentToken.access_token;
    }

    const result = await this.refresh();
    if (result.ok) {
      return result.accessToken;
    }

    // A transient failure leaves the tokens in place, and `isTokenExpired`
    // fires up to 5 minutes early — so the access token in hand may still be
    // inside its real lifetime. Sending it beats sending nothing.
    if (result.reason === 'network') {
      const held = this.getToken();
      const now = Math.floor(Date.now() / 1000);
      if (held && held.expiresAt > now) {
        return held.access_token;
      }
    }
    return null;
  }

  /**
   * Checks if the current session has expired tokens.
   * Returns true if both access and refresh tokens are expired.
   */
  hasExpiredSession(): boolean {
    const tokenData = this.getToken();

    if (!tokenData) {
      return true;
    }

    // If access token is expired, check if refresh token is also expired
    if (this.isTokenExpired(tokenData)) {
      return this.isRefreshTokenExpired(tokenData);
    }

    return false;
  }
}

export const tokenRefreshService = new TokenRefreshService();
